import { useCallback, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { api } from "@/api/client";

// ---- Org brand (tokens + assets) ----

export type BrandTokenView = {
  key: string;
  value: unknown;
  updated_at: string | null;
  updated_by: string | null;
};

export type BrandTokensResponse = {
  org_slug: string;
  is_root: boolean;
  tokens: BrandTokenView[];
};

export type BrandAssetView = {
  id: string;
  kind: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  updated_at: string | null;
  updated_by: string | null;
};

export type BrandAssetsResponse = {
  org_slug: string;
  is_root: boolean;
  assets: BrandAssetView[];
};

export type BrandDocumentView = {
  id: string;
  title: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  updated_at: string | null;
  updated_by: string | null;
};

export type BrandDocumentsResponse = {
  org_slug: string;
  is_root: boolean;
  documents: BrandDocumentView[];
};

/** One of an org's brand profiles. Exactly one per org is primary (new
 *  sessions, the navbar org mark and the legacy ``/brand`` routes use
 *  it). ``env_managed`` marks the root profile fed from RW_BRAND_*. */
export type BrandProfileView = {
  id: string;
  name: string;
  is_primary: boolean;
  display_name: string | null;
  copyright: string | null;
  env_managed: boolean;
  token_count: number;
  asset_count: number;
  document_count: number;
  image_count: number;
  /** FW-47: ``custom`` when the profile changes the rendered look (an
   *  asset, or a look token off the defaults); ``default_only`` when it
   *  holds only default values, like the palette every new org gets. */
  brand_status?: BrandStatus;
  created_at: string | null;
  updated_at: string | null;
};

/** Whether branding is defined (``app.services.brand.status``). */
export type BrandStatus = "custom" | "default_only" | "none";

/** URL base for an org's brand resources. ``profileId`` scopes to one
 *  brand profile; without it the legacy ``/orgs/{slug}/brand`` routes
 *  answer for the org's PRIMARY profile (the navbar org mark and the
 *  setup checklist call the hooks without a profile). */
export function brandBase(slug: string, profileId?: string | null): string {
  return profileId
    ? `/orgs/${slug}/brand/profiles/${profileId}`
    : `/orgs/${slug}/brand`;
}

/** Query key for one profile's brand resource. ``"primary"`` stands in
 *  for the alias routes so a profile id and the alias never share a
 *  cache entry — switching profiles can't show another profile's
 *  cached tokens or preview blob. */
const brandKey = (
  slug: string,
  profileId: string | null | undefined,
  ...rest: string[]
) => ["orgs", slug, "brand", profileId ?? "primary", ...rest];

const brandProfilesKey = (slug: string) =>
  ["orgs", slug, "brand", "profiles"] as const;

/** After a write to one profile: refresh its resources, the primary
 *  alias entries (the hook can't tell whether ``profileId`` IS the
 *  primary, and the AppShell org mark reads the alias; invalidating an
 *  unobserved entry costs nothing), and the profile list (its
 *  token/asset/document counts moved). */
function invalidateBrand(
  qc: QueryClient,
  slug: string,
  profileId: string | null | undefined,
  resources: string[],
): void {
  for (const resource of resources) {
    qc.invalidateQueries({ queryKey: brandKey(slug, profileId, resource) });
    if (profileId) {
      qc.invalidateQueries({ queryKey: brandKey(slug, null, resource) });
    }
  }
  qc.invalidateQueries({ queryKey: brandProfilesKey(slug) });
}

/** Live PDF preview of a sample document rendered with one brand
 *  profile. Resolves to the PDF as a ``Blob``; the card turns the one it
 *  shows into an object URL (``useObjectUrl``) and revokes it when it
 *  stops showing it, so the cache never holds a URL that may be dead.
 *
 *  Stale-while-revalidate:
 *
 *  * **Brand mutations** invalidate this query. TanStack Query keeps the
 *    previous PDF as ``data`` while it refetches (``isFetching``), so
 *    the card keeps showing it with an "Updating…" note. The backend
 *    keeps every render until the brand changes, so a refetch of an
 *    unchanged state answers at once.
 *  * **Kind switch** (DOCX ↔ PPTX): each kind is its own entry, held
 *    for ``gcTime`` while unobserved, so switching back shows that
 *    kind's last preview at once (refetching it if a save invalidated
 *    it meanwhile).
 *  * **Variant switch** keeps showing the same kind's previous PDF as
 *    placeholder data until the variant's own arrives. A kind with no
 *    preview yet gets no placeholder: a deck is never shown as the
 *    document (or the other way round), the card shows its spinner.
 *  * **Refresh button** calls ``forceRefresh`` (below), which sends
 *    ``?bust=<timestamp>`` so the backend renders again from scratch.
 *
 *  The hook keeps a one-shot ``bust`` value in a ref so the Refresh
 *  button can affect just the next request. */
export function useBrandPreview(
  slug: string,
  profileId?: string | null,
  opts: { variant?: string; kind?: "docx" | "pptx" } = {},
) {
  const variant = opts.variant ?? "default";
  const kind = opts.kind ?? "docx";
  const bustRef = useRef<number>(0);
  const query = useQuery({
    queryKey: brandKey(slug, profileId, "preview", variant, kind),
    queryFn: async () => {
      const bust = bustRef.current;
      bustRef.current = 0;  // consume — next call is back to default
      const params = new URLSearchParams({ variant, kind });
      if (bust > 0) params.set("bust", String(bust));
      const url = `${brandBase(slug, profileId)}/preview?${params.toString()}`;
      const r = await api.get<Blob>(url, { responseType: "blob" });
      return r.data;
    },
    enabled: Boolean(slug),
    // Same kind only: the key ends with the kind.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[previousQuery.queryKey.length - 1] === kind
        ? previous
        : undefined,
    // Don't auto-refetch on focus — operator triggers via brand
    // mutations + the manual refresh button.
    staleTime: Infinity,
    // Long enough that flipping DOCX ↔ PPTX in one sitting finds the
    // other kind's last preview.
    gcTime: 30 * 60_000,
  });
  const forceRefresh = useCallback(() => {
    bustRef.current = Date.now();
    return query.refetch();
  }, [query.refetch]);
  return { ...query, forceRefresh };
}

/** One entry of docgen's design-settings registry (``docgen.design``):
 *  what the Brand tab's Design section builds a control from. The
 *  profile's values are its ``design.<key>`` brand tokens. */
export type DesignSettingView = {
  key: string;
  kind: "colour" | "colour_list" | "enum" | "number";
  group: string;
  formats: string[];
  /** ``null`` = the template picks (``format_defaults`` names the
   *  per-format value where one number stands for it). */
  default: string | number | null;
  format_defaults: Record<string, number>;
  choices: string[];
  minimum: number | null;
  maximum: number | null;
  step: number | null;
  unit: string;
  max_items: number;
  label_key: string;
  description: string;
};

/** A brand design value the renderers had to cut: a logo capped for
 *  this logo's shape (``clamped``) or a colour too faint for its
 *  background (``contrast``). Shown to the admin; the agent never hears
 *  of it. */
export type DesignCutView = {
  key: string;
  code: "clamped" | "contrast";
  requested: unknown;
  used: unknown;
  formats: string[];
};

export type BrandDesignResponse = {
  settings: DesignSettingView[];
  /** The brand colours a colour setting may name, resolved for this
   *  profile as ``#RRGGBB`` (the swatches). */
  palette: Record<string, string>;
  cuts?: DesignCutView[];
  /** The server couldn't check the cuts (its probe render failed). */
  cuts_error?: boolean;
};

export function useBrandDesign(slug: string, profileId?: string | null) {
  return useQuery({
    queryKey: brandKey(slug, profileId, "design"),
    queryFn: async () =>
      (await api.get<BrandDesignResponse>(`${brandBase(slug, profileId)}/design`)).data,
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}

export function useBrandTokens(slug: string, profileId?: string | null) {
  return useQuery({
    queryKey: brandKey(slug, profileId, "tokens"),
    queryFn: async () =>
      (
        await api.get<BrandTokensResponse>(
          `${brandBase(slug, profileId)}/tokens`,
        )
      ).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useSetBrandToken(slug: string, profileId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { key: string; value: unknown }) => {
      const r = await api.put<BrandTokenView>(
        `${brandBase(slug, profileId)}/tokens/${encodeURIComponent(payload.key)}`,
        { value: payload.value },
      );
      return r.data;
    },
    // ``design``: the Design section's swatches show the profile's colours.
    onSuccess: () => invalidateBrand(qc, slug, profileId, ["tokens", "preview", "design"]),
  });
}

export function useDeleteBrandToken(slug: string, profileId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      await api.delete(
        `${brandBase(slug, profileId)}/tokens/${encodeURIComponent(key)}`,
      );
    },
    onSuccess: () => invalidateBrand(qc, slug, profileId, ["tokens", "preview", "design"]),
  });
}

export function useBrandAssets(slug: string, profileId?: string | null) {
  return useQuery({
    queryKey: brandKey(slug, profileId, "assets"),
    queryFn: async () =>
      (
        await api.get<BrandAssetsResponse>(
          `${brandBase(slug, profileId)}/assets`,
        )
      ).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useUploadBrandAsset(slug: string, profileId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { kind: string; file: File }) => {
      const fd = new FormData();
      fd.append("file", payload.file);
      const r = await api.put<BrandAssetView>(
        `${brandBase(slug, profileId)}/assets/${encodeURIComponent(payload.kind)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
    onSuccess: () => invalidateBrand(qc, slug, profileId, ["assets", "preview"]),
  });
}

export function useDeleteBrandAsset(slug: string, profileId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (kind: string) => {
      await api.delete(
        `${brandBase(slug, profileId)}/assets/${encodeURIComponent(kind)}`,
      );
    },
    onSuccess: () => invalidateBrand(qc, slug, profileId, ["assets", "preview"]),
  });
}

// ---- Brand reference documents ----
//
// Per-profile reference docs (brand guide, voice-and-tone, …) the agent
// reads as context. They never feed the renderer, so — unlike
// tokens/assets — mutating them never invalidates the brand preview.

export function useBrandDocuments(slug: string, profileId?: string | null) {
  return useQuery({
    queryKey: brandKey(slug, profileId, "documents"),
    queryFn: async () =>
      (
        await api.get<BrandDocumentsResponse>(
          `${brandBase(slug, profileId)}/documents`,
        )
      ).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useUploadBrandDocument(
  slug: string,
  profileId?: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { title: string; file: File }) => {
      const fd = new FormData();
      fd.append("title", payload.title);
      fd.append("file", payload.file);
      const r = await api.post<BrandDocumentView>(
        `${brandBase(slug, profileId)}/documents`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
    onSuccess: () => invalidateBrand(qc, slug, profileId, ["documents"]),
  });
}

export function useDeleteBrandDocument(
  slug: string,
  profileId?: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId: string) => {
      await api.delete(
        `${brandBase(slug, profileId)}/documents/${encodeURIComponent(docId)}`,
      );
    },
    onSuccess: () => invalidateBrand(qc, slug, profileId, ["documents"]),
  });
}


/** Replaces every token + asset of ONE profile (the primary when
 *  ``profileId`` is omitted) with the bundle's contents. Destructive —
 *  the API drops that profile's rows before inserting from the zip;
 *  other profiles are untouched. */
export function useImportBrandBundle(slug: string, profileId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<{ tokens: number; assets: number }> => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post<{ tokens: number; assets: number }>(
        `${brandBase(slug, profileId)}/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
    onSuccess: () =>
      invalidateBrand(qc, slug, profileId, ["tokens", "assets", "preview"]),
  });
}


// ---- Brand profiles ----

/** The org's brand profiles, primary first (the API answers with a
 *  bare list). */
export function useBrandProfiles(slug: string) {
  return useQuery({
    queryKey: brandProfilesKey(slug),
    queryFn: async () =>
      (await api.get<BrandProfileView[]>(`/orgs/${slug}/brand/profiles`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

/** Create a profile — a copy of ``copy_from_profile_id`` (tokens, assets
 *  and reference documents), or blank when it is ``null``. */
export function useCreateBrandProfile(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      copy_from_profile_id: string | null;
    }) =>
      (
        await api.post<BrandProfileView>(
          `/orgs/${slug}/brand/profiles`,
          payload,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: brandProfilesKey(slug) }),
  });
}

/** Rename, set the cover-name / copyright overrides (``null`` clears
 *  one back to the org name), or make a profile primary. */
export function useUpdateBrandProfile(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      profileId: string;
      name?: string;
      display_name?: string | null;
      copyright?: string | null;
      is_primary?: true;
    }) => {
      const { profileId, ...body } = payload;
      return (
        await api.patch<BrandProfileView>(
          `/orgs/${slug}/brand/profiles/${profileId}`,
          body,
        )
      ).data;
    },
    onSuccess: (_data, variables) => {
      if (variables.is_primary) {
        // A primary swap changes what every alias route answers (navbar
        // org mark, setup checklist, the "primary" cache entries):
        // refresh the org's whole brand subtree.
        qc.invalidateQueries({ queryKey: ["orgs", slug, "brand"] });
        return;
      }
      qc.invalidateQueries({ queryKey: brandProfilesKey(slug) });
      // Cover name + copyright feed the sample render.
      qc.invalidateQueries({
        queryKey: brandKey(slug, variables.profileId, "preview"),
      });
    },
  });
}

/** Delete a profile (org admins only; the API refuses the primary and
 *  the environment-managed profile). Its sessions move to the primary;
 *  resolves to how many moved.
 *
 *  Cache order matters. ``onLeave`` runs first so the caller moves the
 *  view (``?profile=``) off the deleted profile. Then that profile's
 *  cached tokens / assets / documents / preview are REMOVED — not
 *  invalidated: a still-mounted observer would refetch them into a 404
 *  and the Branding tab would swap the editor for its load-error alert.
 *  Only then does the profile list refresh. */
export function useDeleteBrandProfile(
  slug: string,
  opts: { onLeave?: (profileId: string) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (profileId: string) =>
      (
        await api.delete<{ moved_sessions: number }>(
          `/orgs/${slug}/brand/profiles/${profileId}`,
        )
      ).data,
    onSuccess: (_data, profileId) => {
      opts.onLeave?.(profileId);
      qc.removeQueries({ queryKey: brandKey(slug, profileId) });
      qc.invalidateQueries({ queryKey: brandProfilesKey(slug) });
      // A queued or ready composer job may have targeted this profile.
      qc.invalidateQueries({ queryKey: ["orgs", slug, "brand", "compose"] });
    },
  });
}
